/**
 * Тонкая обёртка над window.Telegram.WebApp.
 *
 * Мини-апп должен открываться и в обычном браузере - иначе его невозможно
 * отлаживать, - поэтому каждый вызов SDK защищён проверкой.
 */

import { getThemePreference, themeVariables, THEME_STORAGE_KEY } from './theme.js';

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { user?: { id: number; first_name?: string } };
  ready(): void;
  expand(): void;
  close(): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  setBottomBarColor?(color: string): void;
  colorScheme?: 'light' | 'dark';
  themeParams?: Record<string, string | undefined>;
  /** Bot API 8.0 - разворачивает мини-апп во весь экран. */
  requestFullscreen?(): void;
  /** Чтобы свайп вниз не закрывал приложение при прокрутке списка. */
  disableVerticalSwipes?(): void;
  onEvent?(event: string, handler: () => void): void;
  version?: string;
  isVersionAtLeast?(version: string): boolean;
  /** Вырезы устройства: чёлка, «остров», скруглённые углы. */
  safeAreaInset?: { top: number; bottom: number; left: number; right: number };
  /** Место, занятое интерфейсом самого Telegram поверх страницы. */
  contentSafeAreaInset?: { top: number; bottom: number; left: number; right: number };
  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium' | 'heavy'): void;
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
  };
  MainButton: {
    setText(text: string): void;
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
    showProgress(leaveActive?: boolean): void;
    hideProgress(): void;
  };
  BackButton: {
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

export const tg = window.Telegram?.WebApp;

/**
 * Приложение считается запущенным внутри Telegram, если клиент прислал
 * initData. Демо-режим - единственное исключение, и только в dev-сборке.
 */
export const isTelegram =
  Boolean(tg?.initData) ||
  (import.meta.env.DEV && import.meta.env.VITE_MOCK === '1');

export function applyTheme() {
  const theme = themeVariables(tg?.initData ? tg : undefined, window.matchMedia('(prefers-color-scheme: dark)').matches, getThemePreference());
  const root = document.documentElement;
  root.dataset.theme = theme.scheme;
  root.style.colorScheme = theme.scheme;
  for (const [name, value] of Object.entries(theme.vars)) root.style.setProperty(name, value);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme.bg);
  safely('setHeaderColor', () => tg?.setHeaderColor?.(theme.bg));
  safely('setBackgroundColor', () => tg?.setBackgroundColor?.(theme.bg));
  safely('setBottomBarColor', () => tg?.setBottomBarColor?.(theme.bg));
}

let themeInitialized = false;

/**
 * Вызывает метод SDK, переживая старые версии клиента.
 *
 * Проверки `tg.requestFullscreen?.()` недостаточно: метод в объекте есть
 * всегда, но на старом клиенте выбрасывает WebAppMethodUnsupported. Раньше
 * это роняло приложение в белый экран ещё до отрисовки - исключение летело
 * из initTelegram, который вызывается до монтирования React.
 */
function safely(what: string, action: () => void) {
  try {
    action();
  } catch (err) {
    console.info(`[telegram] ${what} недоступен в этой версии клиента:`, err);
  }
}

/**
 * Отдаёт вырезы устройства и панели Telegram в CSS.
 *
 * В полноэкранном режиме своей шапки у Telegram нет, и страница уезжает под
 * часы и чёлку - без этих отступов логотип оказался бы под статус-баром.
 */
function applySafeAreas() {
  if (!tg) return;
  const root = document.documentElement;
  const top = (tg.safeAreaInset?.top ?? 0) + (tg.contentSafeAreaInset?.top ?? 0);
  const bottom = (tg.safeAreaInset?.bottom ?? 0) + (tg.contentSafeAreaInset?.bottom ?? 0);
  root.style.setProperty('--tg-safe-top', `${top}px`);
  root.style.setProperty('--tg-safe-bottom', `${bottom}px`);
}

export function initTelegram() {
  applyTheme();
  if (!themeInitialized) {
    themeInitialized = true;
    tg?.onEvent?.('themeChanged', applyTheme);
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', applyTheme);
    window.addEventListener('cora:theme-preference', applyTheme);
    window.addEventListener('storage', (event) => { if (event.key === THEME_STORAGE_KEY || event.key === null) applyTheme(); });
  }
  if (!tg) return;
  safely('ready', () => tg.ready());
  safely('expand', () => tg.expand());

  // Полный экран - Bot API 8.0. На старых клиентах остаётся expand(),
  // и приложение просто открывается на всю высоту с обычной шапкой.
  if (tg.isVersionAtLeast?.('8.0')) {
    safely('requestFullscreen', () => tg.requestFullscreen?.());
  }
  // Иначе свайп вниз по списку еды закрывал бы приложение
  if (tg.isVersionAtLeast?.('7.7')) {
    safely('disableVerticalSwipes', () => tg.disableVerticalSwipes?.());
  }

  applySafeAreas();
  // Отступы меняются при повороте экрана и переключении полного экрана
  for (const event of ['safeAreaChanged', 'contentSafeAreaChanged', 'fullscreenChanged', 'viewportChanged']) {
    safely(`onEvent ${event}`, () => tg.onEvent?.(event, applySafeAreas));
  }
}

export function haptic(type: 'light' | 'medium' | 'heavy' = 'light') {
  tg?.HapticFeedback?.impactOccurred(type);
}

export function hapticResult(type: 'success' | 'error' | 'warning') {
  tg?.HapticFeedback?.notificationOccurred(type);
}

/** Смещение часового пояса в минутах к востоку от UTC - сервер считает по нему сутки. */
export function tzOffsetMinutes(date?: string): number {
  return -(date ? new Date(`${date}T12:00:00`) : new Date()).getTimezoneOffset();
}
